import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, 
  Calendar, 
  Plus, 
  Users, 
  MapPin, 
  Clock,
  Search,
  LogOut,
  Edit,
  Trash2
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const EventManagement = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    datum: '',
    notiz: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get('/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Fehler beim Laden der Events');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.datum) {
      toast.error('Bitte fuellen Sie alle Pflichtfelder aus');
      return;
    }

    setCreateLoading(true);
    
    try {
      const response = await axios.post('/events', {
        ...formData,
        datum: new Date(formData.datum).toISOString()
      });
      
      setEvents([response.data, ...events]);
      setShowCreateDialog(false);
      setFormData({ name: '', datum: '', notiz: '' });
      toast.success('Event erfolgreich erstellt!');
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Events');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    // Convert ISO date to datetime-local format for input
    const date = new Date(event.datum);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    
    setFormData({
      name: event.name,
      datum: localDate,
      notiz: event.notiz || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.datum) {
      toast.error('Bitte fuellen Sie alle Pflichtfelder aus');
      return;
    }

    setCreateLoading(true);
    
    try {
      const response = await axios.patch(`/events/${selectedEvent.id}`, {
        ...formData,
        datum: new Date(formData.datum).toISOString()
      });
      
      // Update event in list
      setEvents(events.map(e => e.id === selectedEvent.id ? response.data : e));
      setShowEditDialog(false);
      setSelectedEvent(null);
      setFormData({ name: '', datum: '', notiz: '' });
      toast.success('Event erfolgreich aktualisiert!');
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren des Events');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteClick = (event) => {
    setSelectedEvent(event);
    setShowDeleteDialog(true);
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    try {
      await axios.delete(`/events/${selectedEvent.id}`);
      
      // Remove event from list
      setEvents(events.filter(e => e.id !== selectedEvent.id));
      setShowDeleteDialog(false);
      setSelectedEvent(null);
      toast.success('Event erfolgreich gelöscht!');
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen des Events');
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd.MM.yyyy', { locale: de });
    } catch (error) {
      return 'Ungueltiges Datum';
    }
  };

  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'HH:mm', { locale: de });
    } catch (error) {
      return '';
    }
  };

  const getEventStatus = (eventDate) => {
    const now = new Date();
    const event = new Date(eventDate);
    const diffDays = Math.ceil((event - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { label: 'Beendet', color: 'bg-gray-100 text-gray-700' };
    } else if (diffDays === 0) {
      return { label: 'Heute', color: 'bg-red-100 text-red-700' };
    } else if (diffDays <= 7) {
      return { label: `In ${diffDays} Tagen`, color: 'bg-amber-100 text-amber-700' };
    } else {
      return { label: `In ${diffDays} Tagen`, color: 'bg-blue-100 text-blue-700' };
    }
  };

  const filteredEvents = events.filter(event =>
    event.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Navigation Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {user.role === 'admin' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/')}
                  className="flex items-center space-x-2"
                  data-testid="back-to-dashboard-button"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Dashboard</span>
                </Button>
              )}
              <div>
                <h1 className="text-xl font-bold font-space-grotesk text-gray-900">
                  Event-Verwaltung
                </h1>
                <p className="text-sm text-gray-600">Verwalten Sie alle Heimspiele und Events</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.email}</p>
                <p className="text-xs text-gray-500 capitalize">
                  {user.role === 'admin' ? 'Administrator' : 'Hostess'}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                className="flex items-center space-x-2 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span>Abmelden</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Controls Section */}
        <div className="mb-8 space-y-6">
          {/* Search and Create */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Events durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 transition-all duration-200 focus:ring-2 focus:ring-amber-500/20"
                data-testid="search-events-input"
              />
            </div>
            
            {user.role === 'admin' && (
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg"
                    data-testid="create-event-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Neues Event erstellen
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px] animate-scale-in" data-testid="create-event-dialog">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-gray-900">
                      Neues Event erstellen
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                      Erstellen Sie ein neues Heimspiel oder Event mit allen Details.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateEvent} className="space-y-6 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                        Event-Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="z.B. KEC vs. Duesseldorfer EG"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500/20"
                        data-testid="event-name-input"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="datum" className="text-sm font-medium text-gray-700">
                        Datum und Uhrzeit *
                      </Label>
                      <Input
                        id="datum"
                        name="datum"
                        type="datetime-local"
                        value={formData.datum}
                        onChange={handleInputChange}
                        className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500/20"
                        data-testid="event-date-input"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="notiz" className="text-sm font-medium text-gray-700">
                        Notizen (optional)
                      </Label>
                      <Textarea
                        id="notiz"
                        name="notiz"
                        placeholder="Zusaetzliche Informationen zum Event..."
                        value={formData.notiz}
                        onChange={handleInputChange}
                        rows={3}
                        className="resize-none transition-all duration-200 focus:ring-2 focus:ring-emerald-500/20"
                        data-testid="event-notes-input"
                      />
                    </div>
                    
                    <div className="flex justify-end space-x-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowCreateDialog(false)}
                        disabled={createLoading}
                        data-testid="cancel-create-event-button"
                      >
                        Abbrechen
                      </Button>
                      <Button
                        type="submit"
                        disabled={createLoading}
                        className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                        data-testid="submit-create-event-button"
                      >
                        {createLoading ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                            Erstellen...
                          </div>
                        ) : (
                          'Event erstellen'
                        )}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Events Grid */}
        {filteredEvents.length === 0 ? (
          <div className="text-center py-16 animate-fade-in" data-testid="no-events-message">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mb-4">
              <Calendar className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm ? 'Keine Events gefunden' : 'Noch keine Events vorhanden'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm 
                ? `Keine Events entsprechen der Suche "${searchTerm}"`
                : 'Erstellen Sie Ihr erstes Event, um zu beginnen.'
              }
            </p>
            {!searchTerm && user.role === 'admin' && (
              <Button 
                onClick={() => setShowCreateDialog(true)}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                data-testid="create-first-event-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Erstes Event erstellen
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map((event, index) => {
              const status = getEventStatus(event.datum);
              
              return (
                <Card 
                  key={event.id}
                  className="card transform hover:scale-[1.02] transition-all duration-200 bg-white border-0 shadow-lg hover:shadow-xl animate-slide-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`event-card-${index}`}
                >
                  <CardHeader className="pb-3">
                    <div 
                      className="flex items-start justify-between cursor-pointer"
                      onClick={() => navigate(`/events/${event.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg font-semibold text-gray-900 mb-2 truncate">
                          {event.name}
                        </CardTitle>
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span>{formatDate(event.datum)}</span>
                          <Clock className="w-4 h-4 ml-4 mr-1 flex-shrink-0" />
                          <span>{formatTime(event.datum)}</span>
                        </div>
                      </div>
                      <Badge className={`${status.color} text-xs font-medium flex-shrink-0`}>
                        {status.label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="cursor-pointer"
                      onClick={() => navigate(`/events/${event.id}`)}
                    >
                      {event.notiz && (
                        <CardDescription className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {event.notiz}
                        </CardDescription>
                      )}
                      
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          <span>VIP-Bereich</span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            Event Details
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    {user.role === 'admin' && (
                      <div className="flex items-center space-x-2 pt-3 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditEvent(event);
                          }}
                          className="flex-1 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Bearbeiten
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(event);
                          }}
                          className="flex-1 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Loeschen
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Event Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Event bearbeiten</DialogTitle>
            <DialogDescription>
              Aendern Sie Datum und Uhrzeit des Events
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateEvent} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Eventname *</Label>
              <Input
                id="edit-name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="z.B. VIP Heimspiel vs. Bayern"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-datum">Datum und Uhrzeit *</Label>
              <Input
                id="edit-datum"
                name="datum"
                type="datetime-local"
                value={formData.datum}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-notiz">Notiz</Label>
              <Textarea
                id="edit-notiz"
                name="notiz"
                value={formData.notiz}
                onChange={handleInputChange}
                placeholder="Zusaetzliche Informationen..."
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowEditDialog(false);
                  setSelectedEvent(null);
                  setFormData({ name: '', datum: '', notiz: '' });
                }}
              >
                Abbrechen
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={createLoading}
              >
                {createLoading ? 'Speichern...' : 'Speichern'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Event Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Event loeschen</DialogTitle>
            <DialogDescription>
              Moechten Sie dieses Event wirklich loeschen? Diese Aktion kann nicht rueckgaengig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          {selectedEvent && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-1">{selectedEvent.name}</h4>
                <p className="text-sm text-gray-600">
                  {formatDate(selectedEvent.datum)} um {formatTime(selectedEvent.datum)}
                </p>
              </div>
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  ⚠️ Alle Buchungen und Tische dieses Events werden ebenfalls geloescht!
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end space-x-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setShowDeleteDialog(false);
                setSelectedEvent(null);
              }}
            >
              Abbrechen
            </Button>
            <Button 
              onClick={handleDeleteEvent}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Loeschen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventManagement;